import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sunrise, Sun, Moon, Calendar, Instagram, Printer } from 'lucide-react';
import BlogSketch from './BlogSketch';
import {
  CALENDAR_MONTHS,
  WEEKLY_STORIES,
  PILLAR_COLORS,
  PILLAR_LABEL,
  CAL_STATS,
  PHASES,
} from '../data/editorialCalendar';
import { useSeo } from '../hooks/useSeo';

const SLOT_ICON = { mattina: Sunrise, pomeriggio: Sun, sera: Moon };

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' });
}

function PostCard({ post }) {
  const SlotIcon = SLOT_ICON[post.slot];
  const color = PILLAR_COLORS[post.pillar];
  const phase = PHASES[post.phase];

  const visual = (() => {
    if (post.kind === 'photo') {
      return <div className="cal-card__visual"><img src={post.media.src} alt="" loading="lazy" /></div>;
    }
    if (post.kind === 'sketch' || post.kind === 'carousel') {
      return (
        <div className="cal-card__visual cal-card__visual--sketch">
          <BlogSketch slug={post.media.slug} />
          {post.kind === 'carousel' && (
            <span className="cal-card__badge">carosello</span>
          )}
        </div>
      );
    }
    return (
      <div className="cal-card__visual cal-card__visual--quote">
        <span className="font-display-italic">{post.media.text}</span>
      </div>
    );
  })();

  return (
    <article className="cal-card" style={{ '--pillar': color }}>
      {phase && (
        <span className="cal-card__phase" style={{ background: phase.color }}>
          {phase.label}
        </span>
      )}
      {visual}
      <div className="cal-card__body">
        <div className="cal-card__meta">
          <span className="cal-card__day">
            {post.dayName} · {formatDate(post.day).split(' ')[0]}
          </span>
          <span className="cal-card__slot" title={post.slot}>
            <SlotIcon size={12} />
          </span>
        </div>
        <p className="cal-card__title font-display">{post.title}</p>
        <p className="cal-card__hook">{post.hook}</p>
        <span className="cal-card__pillar" style={{ color }}>
          <span className="dot" style={{ background: color }} />
          {PILLAR_LABEL[post.pillar]}
          <span className="opacity-50">·</span>
          <span className="capitalize">{post.kind}</span>
        </span>
      </div>
    </article>
  );
}

export default function EditorialCalendar() {
  const [active, setActive] = useState('ago');
  const month = useMemo(() => CALENDAR_MONTHS.find((m) => m.id === active), [active]);

  useSeo({
    title: 'Piano editoriale Instagram — Lempole (agosto → ottobre 2026)',
    description: 'Calendario editoriale 3 mesi per @lempole.wine: 27 post + storie ricorrenti, distribuiti su 4 pilastri. Invaiatura, vendemmia, fermento.',
    canonical: 'https://lempole.com/editorial-calendar',
    type: 'website',
  });

  const totalPosts = CALENDAR_MONTHS.reduce((n, m) => n + m.posts.length, 0);
  const totalStories = CAL_STATS.reduce((n, s) => n + s.stories, 0);

  return (
    <div className="cal-page">
      {/* HEADER BAR */}
      <div className="cal-topbar">
        <Link to="/instagram-preview" className="cal-back" data-testid="cal-back">
          <ArrowLeft size={16} /> profilo IG
        </Link>
        <span className="cal-note font-caveat">
          piano editoriale · agosto → ottobre 2026
        </span>
        <button
          type="button"
          className="cal-print"
          onClick={() => window.print()}
          data-testid="cal-print"
          aria-label="Stampa calendario"
        >
          <Printer size={14} /> stampa
        </button>
      </div>

      {/* HERO */}
      <header className="cal-hero">
        <span className="eyebrow">calendario editoriale</span>
        <h1 className="cal-hero__title font-display">
          Tre mesi di <em className="font-display-italic">calma costante</em>.
        </h1>
        <p className="cal-hero__lead">
          Agosto → Ottobre 2026. Un piano che accompagna il vero lancio: 5 giorni
          di <em className="font-display-italic">teaser</em> sul bradipo, 10 giorni
          di <em className="font-display-italic">reveal</em> del progetto, poi il
          diario in diretta con la vendemmia. Frequenza intensa nella fase di
          lancio (1 post al giorno), più rilassata nei giorni di cantina.
        </p>
        <ul className="cal-hero__stats">
          <li><b>{totalPosts}</b><span>post totali</span></li>
          <li><b>{totalStories}</b><span>storie previste</span></li>
          <li><b>3</b><span>mesi</span></li>
          <li><b>4</b><span>pilastri</span></li>
        </ul>
      </header>

      {/* PHASES LEGEND */}
      <div className="cal-phases">
        {Object.entries(PHASES).map(([key, ph]) => (
          <div key={key} className="cal-phase">
            <span className="cal-phase__dot" style={{ background: ph.color }} />
            <div>
              <p className="cal-phase__label font-kicker">{ph.label}</p>
              <p className="cal-phase__dates">{ph.dates}</p>
              <p className="cal-phase__freq">{ph.frequency}</p>
            </div>
          </div>
        ))}
      </div>

      {/* MONTH TABS */}
      <div className="cal-tabs">
        {CALENDAR_MONTHS.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`cal-tab ${active === m.id ? 'is-active' : ''}`}
            onClick={() => setActive(m.id)}
            data-testid={`cal-tab-${m.id}`}
          >
            <span className="cal-tab__month">{m.name}</span>
            <span className="cal-tab__sub font-display-italic">{m.subtitle}</span>
          </button>
        ))}
      </div>

      {/* MONTH BODY */}
      <section className="cal-month" data-month={month.id}>
        <p className="cal-month__intro font-display-italic">{month.intro}</p>

        <h2 className="cal-month__section font-kicker">
          <Instagram size={12} /> Post ({month.posts.length})
        </h2>

        <div className="cal-grid" data-testid={`cal-grid-${month.id}`}>
          {month.posts.map((p, i) => (
            <PostCard key={i} post={p} />
          ))}
        </div>

        <h2 className="cal-month__section font-kicker">
          <Calendar size={12} /> Storie ricorrenti (× 4 settimane)
        </h2>

        <div className="cal-stories">
          {WEEKLY_STORIES.map((s, i) => {
            const SlotIcon = SLOT_ICON[s.slot];
            return (
              <div key={i} className="cal-story">
                <div className="cal-story__head">
                  <span className="cal-story__day font-kicker">{s.day}</span>
                  <SlotIcon size={14} />
                </div>
                <p className="cal-story__label font-display">{s.label}</p>
                <p className="cal-story__idea">{s.idea}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* LEGEND */}
      <footer className="cal-legend">
        <h2 className="font-display">Come leggere questo piano</h2>
        <p>
          Ogni mese ha un tema stagionale — <b>invaiatura, vendemmia, fermento</b>.
          I post sono suggeriti nel loro giorno e slot orario ideale, ma restano
          spostabili. Se una settimana è impegnativa, prova a pubblicare almeno
          il post del <b>giovedì</b> e una storia il <b>venerdì</b>: quello è il
          minimo indispensabile per rimanere presente senza forzare.
        </p>
        <ul className="cal-pillars">
          {Object.entries(PILLAR_LABEL).map(([k, label]) => (
            <li key={k}>
              <span className="dot" style={{ background: PILLAR_COLORS[k] }} />
              <b>{label}</b>
            </li>
          ))}
        </ul>
        <p className="cal-tip font-caveat">
          Consiglio: prepara i contenuti la <em>domenica sera</em>, batch, e programma
          con calma per la settimana. Con questa cadenza non pubblichi mai in fretta.
        </p>
      </footer>
    </div>
  );
}
